// Minimal i18n helper for sidebar localization.
//
// Default locale = 'id' (Indonesian, primary thesis language).
// Switching to 'en' overlays the English label/helper for sidebar
// items + group headers. View bodies stay in Indonesian (deliberate
// scope cap — translating every view's dense text is Phase G work).
//
// Locale persisted to localStorage under key IFI_LOCALE.

export type Locale = 'id' | 'en';

const STORAGE_KEY = 'IFI_LOCALE';

export function getLocale(): Locale {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'en' ? 'en' : 'id';
  } catch {
    return 'id';
  }
}

export function setLocale(loc: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, loc);
  } catch {
    /* noop */
  }
}

/**
 * Pick localized field with fallback to Indonesian.
 *   navTextOf({ id, label, helper }, 'label')
 * If locale=en and an English string is registered for `id` in
 * NAV_EN, returns the English string; otherwise the Indonesian
 * `label` / `helper` from the original NavItem/NavGroup.
 */
export function navTextOf(
  obj: { id?: string; label?: string; helper?: string; labelEn?: string; helperEn?: string },
  baseField: 'label' | 'helper',
): string {
  const locale = getLocale();
  if (locale === 'en') {
    // First: dictionary lookup by id
    if (obj.id) {
      const en = NAV_EN[obj.id];
      if (en) {
        const v = baseField === 'label' ? en.label : en.helper;
        if (v) return v;
      }
    }
    // Second: inline labelEn/helperEn field if any
    const enInline = baseField === 'label' ? obj.labelEn : obj.helperEn;
    if (typeof enInline === 'string' && enInline.trim()) return enInline;
  }
  const base = baseField === 'label' ? obj.label : obj.helper;
  return typeof base === 'string' ? base : '';
}

/** Short locale label for UI badges. */
export function localeLabel(loc: Locale): string {
  return loc === 'en' ? 'EN' : 'ID';
}

/**
 * Bilingual string selector for view-body translations.
 * Usage: `t({ id: 'Halo dunia', en: 'Hello world' })`.
 * Returns the English string when locale=en, else the Indonesian.
 * Falls back to ID if EN is missing (and vice-versa).
 */
export function t(pair: { id: string; en?: string }): string {
  if (getLocale() === 'en' && pair.en) return pair.en;
  return pair.id;
}

/**
 * English overlay dictionary for sidebar items. Keys = NavItem.id /
 * NavGroup.id. Update here when a new nav entry needs EN coverage.
 * Missing keys → Indonesian fallback.
 */
export const NAV_EN: Record<string, { label?: string; helper?: string }> = {
  // Groups also keyed by their id (ThesisStage strings). NavGroup
  // entries already carry labelEn/helperEn inline, so this dictionary
  // is primarily for NavItem entries.

  // G1 Orientation
  'research-overview':           { label: 'Research Overview',          helper: 'Goals, research questions, QUAL→QUAN flow, unit of analysis.' },
  'research-workflow-tracker':   { label: 'Research Workflow',          helper: '10 thesis stages from planning to defense; status per stage.' },
  'methodology-explainer':       { label: 'Methodology & Formulas',     helper: 'Mixed-methods, BWM, CAUSE/IMPACT decoupling, 2×2 matrix.' },
  'literature-review':           { label: 'Literature Review',          helper: 'Thesis references actually used as the INFRAIMPACT foundation (curated, not full master).' },

  // G2 Data Collection
  'data-collection-hub':         { label: 'Data Collection Hub',        helper: 'Landing for source_person + research team assigned tasks.' },
  'raw-viewer':                  { label: 'Raw Asset Database',         helper: 'Excel-style inline edit of asset master records. Delete → 30-day Bin.' },
  'expert-judgment-bwm':         { label: 'Expert Judgment (BWM)',      helper: 'Expert preferences input (Best-Worst) — entry point for respondents.' },
  'interview-form':              { label: 'Interview',                  helper: 'Semi-structured interview guide and notes.' },
  'fgd-form':                    { label: 'Focus Group Discussion',     helper: 'FGD guide and findings summary.' },
  'field-form':                  { label: 'Field Observation',          helper: 'Asset condition, service status, operational evidence.' },
  'doc-form':                    { label: 'Document Review',            helper: 'Evidence document checklist (DIPA, contract, regulation, etc.).' },

  // G3 Validation
  'raw-data-manager':            { label: 'Raw Data Manager',           helper: 'Curate raw_asset_data before it enters indicator scoring.' },
  'data-validation':             { label: 'Data Validation',            helper: 'Validation state machine: field_confirmed / corrected / rejected / returned_for_revision.' },
  'cvi-aiken-panel':             { label: 'Validity Panel (CVI/Aiken)', helper: 'Per-rater × indicator relevance 1-4; auto Aiken V + I-CVI.' },
  'thematic-coding':             { label: 'Thematic Coding',            helper: 'Codebook + quote-level codings (institutional void, fiscal mismatch, …).' },
  'triangulation-matrix':        { label: 'Triangulation Matrix',       helper: 'Auto-aggregates cause_tags + impact_tags across interviews/FGDs/docs.' },
  'quality':                     { label: 'Data Quality',               helper: 'Asset coverage + coding progress + score coverage + recent audit log.' },

  // G4 Weighting
  'bwm-studio':                  { label: 'BWM Studio',                 helper: '9-step Rezaei wizard + geometric-mean panel aggregation.' },
  'bwm-results-review':          { label: 'Weights Review',             helper: 'Open BWM Studio to review saved panel weights + consistency ratio.' },

  // G5 Scoring
  'indicator-registry':          { label: 'Indicator Registry',         helper: 'Definitions, formulas, thresholds, lifecycle of indicators.' },
  'calc-studio':                 { label: 'INFRAIMPACT Computation',    helper: '8-step trace: raw → normalize → weight → S_CAUSE/S_IMPACT → quadrant.' },

  // G6 Diagnosis
  'diagnostic-dashboard':        { label: 'Diagnostic Dashboard',       helper: 'Open the public dashboard (2×2 matrix, map, trend, ranking) in a new tab.' },
  'asset-case-review':           { label: 'Asset Case Review',          helper: 'n=3 proof-of-concept (optimal/median/severe) — tests framework differentiation.' },
  'policy-insight':              { label: 'Policy Insight',             helper: 'Managerial &amp; policy implications — for high-level decision makers.' },

  // G7 Reporting
  'export-center':               { label: 'Export Center',              helper: 'CSV download per table + JSON backup snapshot.' },
  'thesis-tables':               { label: 'Thesis Tables',              helper: 'Open Export Center — CSVs ready for thesis appendix.' },

  // G8 Administration
  'admin-users':                 { label: 'User Management',            helper: 'Invite, change role, activate/deactivate. Super_admin only.' },
  'assignments-admin':           { label: 'Task Management',            helper: 'Create + manage data-collection tasks per source_person.' },
  'settings':                    { label: 'System Settings',            helper: 'Session info, default method, table counts, doc links.' },
  'audit-log-viewer':            { label: 'Audit Log',                  helper: 'audit_logs (event log) + data_change_audit (SHA256 hash chain).' },
};
